"use client"

import * as React from "react"

import type { QuestionImportDocs } from "@/hooks/use-question-import"
import { importedLibraryItems } from "@/lib/question-import-to-library"

/**
 * Imported bank row ids that landed after this page session started.
 *
 * Persisted commits from an earlier visit are seeded into the baseline on the
 * first effect pass so they do not read as new. Commits that arrive later in
 * the same session (direct import settle, review take into bank) accumulate in
 * the returned set until the tab closes.
 */
export function useLibrarySessionNewImportIds(
  docs: QuestionImportDocs
): ReadonlySet<string> {
  const [sessionNewIds, setSessionNewIds] = React.useState<ReadonlySet<string>>(
    () => new Set()
  )
  const commitAtByJobRef = React.useRef<Record<string, number>>({})
  const initializedRef = React.useRef(false)

  React.useEffect(() => {
    if (!initializedRef.current) {
      for (const record of Object.values(docs)) {
        commitAtByJobRef.current[record.id] = record.commit?.at ?? 0
      }
      initializedRef.current = true
      return
    }

    const nextIds = new Set<string>()
    for (const record of Object.values(docs)) {
      const commitAt = record.commit?.at ?? 0
      const seenAt = commitAtByJobRef.current[record.id] ?? 0
      if (commitAt <= seenAt) continue
      commitAtByJobRef.current[record.id] = commitAt
      for (const row of importedLibraryItems({ [record.id]: record })) {
        nextIds.add(row.id)
      }
    }

    if (nextIds.size === 0) return
    setSessionNewIds((current) => {
      const merged = new Set(current)
      for (const id of nextIds) merged.add(id)
      return merged
    })
  }, [docs])

  return sessionNewIds
}
